import { apiClient } from './client';
import type {
  WellPredictionsResponse,
  SiteConcentrationSummary,
  ConcentrationRisk,
  PortfolioConcentrationSummary,
  TimelinePredictionsResponse,
  HybridRankingResponse,
} from '@/types';

export interface WellPredictionFilters {
  analyte_code?: string;
  well_id?: string;
  analyte_group?: string;
  page?: number;
  page_size?: number;
}

export const concentrationApi = {
  /** Get latest per-well predictions for a site (paginated) */
  getWellPredictions: async (
    siteId: string,
    filters?: WellPredictionFilters
  ): Promise<WellPredictionsResponse> => {
    const response = await apiClient.get<WellPredictionsResponse>(
      `/concentration/sites/${siteId}/well-predictions`,
      { params: filters }
    );
    return response.data;
  },

  /** Get site-level closure timeline and full risk summary */
  getClosureTimeline: async (
    siteId: string
  ): Promise<SiteConcentrationSummary> => {
    const response = await apiClient.get<SiteConcentrationSummary>(
      `/concentration/sites/${siteId}/closure-timeline`
    );
    return response.data;
  },

  /** Get lightweight concentration risk score (no trajectory payload) */
  getSiteRisk: async (
    siteId: string
  ): Promise<ConcentrationRisk> => {
    const response = await apiClient.get<ConcentrationRisk>(
      `/concentration/sites/${siteId}/risk`
    );
    return response.data;
  },

  /** Get concentration risk summary for all sites in portfolio */
  getPortfolioSummary: async (): Promise<PortfolioConcentrationSummary> => {
    const response = await apiClient.get<PortfolioConcentrationSummary>(
      '/concentration/portfolio/summary'
    );
    return response.data;
  },

  /** Get hybrid-scored portfolio ranking combining wells-in-danger and cost urgency */
  getHybridRanking: async (): Promise<HybridRankingResponse> => {
    const response = await apiClient.get<HybridRankingResponse>(
      '/concentration/portfolio/hybrid-ranking'
    );
    return response.data;
  },

  /** Get well predictions indexed by month for the timeline slider */
  getTimelinePredictions: async (
    siteId: string
  ): Promise<TimelinePredictionsResponse> => {
    const response = await apiClient.get<TimelinePredictionsResponse>(
      `/concentration/sites/${siteId}/timeline-predictions`
    );
    return response.data;
  },
};
